import React, { useState, useEffect } from 'react';
import {
    Container,
    Card,
    CardContent,
    Typography,
    Grid,
    Button,
    TextField,
    LinearProgress,
    Box,
    ButtonGroup
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import {
    Elements,
    CardNumberElement,
    CardExpiryElement,
    CardCvcElement,
    useStripe,
    useElements
} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useParams, useHistory } from 'react-router-dom';
import config from '../config';
import { doSwaggerCall } from '../hooks/useSwaggerClient';
import { useNotify } from '../hooks/useNotify';

const stripePromise = loadStripe(config.stripeKey);

const CARD_ELEMENT_OPTIONS = {
    style: {
        base: {
            fontSize: '16px',
            color: '#424770',
            '::placeholder': {
                color: '#aab7c4'
            },
            fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif'
        },
        invalid: {
            color: '#9e2146'
        }
    }
};

const TIP_PRESETS = [
    { label: '10%', value: 0.1 },
    { label: '15%', value: 0.15 },
    { label: '20%', value: 0.2 },
    { label: '25%', value: 0.25 }
];

// Separate component for card form that needs to be inside Elements
function CardForm({ onTokenCreated, disabled }) {
    const stripe = useStripe();
    const elements = useElements();
    const { notifyError } = useNotify();
    const [cardHolderName, setCardHolderName] = useState('');
    const [cardComplete, setCardComplete] = useState({
        number: false,
        expiry: false,
        cvc: false
    });
    const [processing, setProcessing] = useState(false);

    const handleAddCard = async () => {
        if (!stripe || !elements || !cardHolderName) {
            return;
        }

        setProcessing(true);
        try {
            const cardElement = elements.getElement(CardNumberElement);
            const result = await stripe.createToken(cardElement, {
                name: cardHolderName
            });

            if (result.error) {
                notifyError(result.error.message);
                setProcessing(false);
                return;
            }

            if (result.token) {
                onTokenCreated({
                    stripe_last4: result.token.card.last4,
                    stripe: result.token
                });
            }
        } catch (err) {
            notifyError('Failed to create payment token');
            setProcessing(false);
        }
    };

    const isFormComplete =
        cardHolderName && cardComplete.number && cardComplete.expiry && cardComplete.cvc;

    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <Typography variant="subtitle2" style={{ marginBottom: '8px', fontWeight: 500 }}>
                    Card Number
                </Typography>
                <Box p={1.5} border="1px solid #ccc" borderRadius="4px" bgcolor="white">
                    <CardNumberElement
                        options={CARD_ELEMENT_OPTIONS}
                        onChange={e => setCardComplete({ ...cardComplete, number: e.complete })}
                    />
                </Box>
            </Grid>
            <Grid item xs={12}>
                <Typography variant="subtitle2" style={{ marginBottom: '8px', fontWeight: 500 }}>
                    Cardholder Name
                </Typography>
                <TextField
                    fullWidth
                    variant="outlined"
                    value={cardHolderName}
                    onChange={e => setCardHolderName(e.target.value)}
                    placeholder="Name on card"
                    disabled={disabled || processing}
                />
            </Grid>
            <Grid item xs={6}>
                <Typography variant="subtitle2" style={{ marginBottom: '8px', fontWeight: 500 }}>
                    Expiration Date
                </Typography>
                <Box p={1.5} border="1px solid #ccc" borderRadius="4px" bgcolor="white">
                    <CardExpiryElement
                        options={CARD_ELEMENT_OPTIONS}
                        onChange={e => setCardComplete({ ...cardComplete, expiry: e.complete })}
                    />
                </Box>
            </Grid>
            <Grid item xs={6}>
                <Typography variant="subtitle2" style={{ marginBottom: '8px', fontWeight: 500 }}>
                    CVC
                </Typography>
                <Box p={1.5} border="1px solid #ccc" borderRadius="4px" bgcolor="white">
                    <CardCvcElement
                        options={CARD_ELEMENT_OPTIONS}
                        onChange={e => setCardComplete({ ...cardComplete, cvc: e.complete })}
                    />
                </Box>
            </Grid>
            <Grid item xs={12}>
                <Button
                    fullWidth
                    variant="outlined"
                    color="primary"
                    onClick={handleAddCard}
                    disabled={!isFormComplete || disabled || processing}
                    style={{ marginTop: '8px' }}
                >
                    {processing ? 'Verifying Card...' : 'Verify Card'}
                </Button>
            </Grid>
        </Grid>
    );
}

function PaymentLinkPage() {
    const { token } = useParams();
    const history = useHistory();
    const { notifySuccess, notifyError } = useNotify();

    const [loading, setLoading] = useState(true);
    const [paying, setPaying] = useState(false);
    const [paymentLink, setPaymentLink] = useState(null);
    const [error, setError] = useState(null);
    const [tipType, setTipType] = useState('none'); // 'none', 'preset', 'custom'
    const [selectedPreset, setSelectedPreset] = useState(null);
    const [customTip, setCustomTip] = useState('');
    const [stripeToken, setStripeToken] = useState(null);

    useEffect(() => {
        async function fetchPaymentLink() {
            try {
                const response = await doSwaggerCall('balance', 'getPaymentLinkByToken', {
                    token
                });
                if (response.status === 'success') {
                    setPaymentLink(response.data);
                }
            } catch (err) {
                console.error('Error fetching payment link:', err);
                setError(err?.message || 'Payment link not found or has expired');
            } finally {
                setLoading(false);
            }
        }

        fetchPaymentLink();
    }, [token]);

    const calculateTipAmount = () => {
        if (!paymentLink) {
            return 0;
        }
        const baseAmount = parseFloat(paymentLink.amount);

        if (tipType === 'preset' && selectedPreset !== null) {
            return baseAmount * selectedPreset;
        }
        if (tipType === 'custom' && customTip) {
            return parseFloat(customTip) || 0;
        }
        return 0;
    };

    const getTotalAmount = () => {
        if (!paymentLink) {
            return 0;
        }
        return parseFloat(paymentLink.amount) + calculateTipAmount();
    };

    const handlePresetClick = value => {
        setTipType('preset');
        setSelectedPreset(value);
        setCustomTip('');
    };

    const handleCustomTipChange = e => {
        setTipType('custom');
        setSelectedPreset(null);
        setCustomTip(e.target.value);
    };

    const handleNoTip = () => {
        setTipType('none');
        setSelectedPreset(null);
        setCustomTip('');
    };

    const handlePayment = async () => {
        if (!stripeToken || !stripeToken.stripe) {
            notifyError('Please add your card information first');
            return;
        }

        const tipAmount = calculateTipAmount();
        const totalAmount = getTotalAmount();

        if (totalAmount <= 0) {
            notifyError('Invalid payment amount');
            return;
        }

        setPaying(true);
        try {
            const response = await doSwaggerCall('balance', 'completePaymentLink', {
                token,
                paymentData: {
                    stripeToken: stripeToken.stripe.id,
                    amount: totalAmount,
                    tipAmount
                }
            });

            if (response.status === 'success') {
                notifySuccess('Payment completed successfully!');
                // Redirect to success page or show success message
                setTimeout(() => {
                    history.push('/');
                }, 2000);
            }
        } catch (err) {
            console.error('Error processing payment:', err);
            notifyError(err?.message || 'Failed to process payment');
        } finally {
            setPaying(false);
        }
    };

    if (loading) {
        return (
            <Box style={{ minHeight: '100vh', backgroundColor: '#f5f5f5', padding: '40px 20px' }}>
                <Container maxWidth="sm">
                    <Card elevation={3} style={{ borderRadius: '12px' }}>
                        <CardContent style={{ padding: '48px 32px', textAlign: 'center' }}>
                            <Typography
                                variant="h5"
                                gutterBottom
                                style={{ fontWeight: 600, marginBottom: '24px' }}
                            >
                                Loading Payment Details...
                            </Typography>
                            <LinearProgress style={{ borderRadius: '4px', height: '6px' }} />
                        </CardContent>
                    </Card>
                </Container>
            </Box>
        );
    }

    if (error || !paymentLink) {
        return (
            <Box style={{ minHeight: '100vh', backgroundColor: '#f5f5f5', padding: '40px 20px' }}>
                <Container maxWidth="sm">
                    <Card elevation={3} style={{ borderRadius: '12px' }}>
                        <CardContent style={{ padding: '32px' }}>
                            <Alert severity="error" style={{ borderRadius: '8px' }}>
                                <Typography variant="h6" style={{ fontWeight: 600 }}>
                                    {error || 'Payment link not found'}
                                </Typography>
                            </Alert>
                        </CardContent>
                    </Card>
                </Container>
            </Box>
        );
    }

    const company = paymentLink.company || {};
    const tipAmount = calculateTipAmount();
    const totalAmount = getTotalAmount();

    return (
        <Box style={{ minHeight: '100vh', backgroundColor: '#f5f5f5', padding: '40px 20px' }}>
            <Container maxWidth="sm">
                <Card elevation={3} style={{ borderRadius: '12px' }}>
                    <CardContent style={{ padding: '32px' }}>
                        {/* Company Branding */}
                        {company.logo && (
                            <Box textAlign="center" mb={3}>
                                <img
                                    src={company.logo}
                                    alt={company.name}
                                    style={{ maxHeight: '100px', maxWidth: '250px' }}
                                />
                            </Box>
                        )}

                        <Typography
                            variant="h4"
                            align="center"
                            gutterBottom
                            style={{ fontWeight: 600 }}
                        >
                            {company.name || 'Payment Request'}
                        </Typography>

                        <Typography
                            variant="body2"
                            align="center"
                            color="textSecondary"
                            gutterBottom
                            style={{ marginBottom: '24px' }}
                        >
                            Secure payment powered by Stripe
                        </Typography>

                        {/* Payment Amount */}
                        <Box
                            my={3}
                            p={3}
                            bgcolor="#f8f9fa"
                            borderRadius="8px"
                            style={{ border: '1px solid #e0e0e0' }}
                        >
                            <Grid container spacing={2} alignItems="center">
                                <Grid item xs={6}>
                                    <Typography
                                        variant="body1"
                                        style={{ fontWeight: 500, color: '#555' }}
                                    >
                                        Amount Due:
                                    </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography
                                        variant="h5"
                                        align="right"
                                        style={{ fontWeight: 600 }}
                                    >
                                        ${parseFloat(paymentLink.amount).toFixed(2)}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="caption" color="textSecondary">
                                        Balance Type:{' '}
                                        {paymentLink.balance_name || paymentLink.balance_slug}
                                    </Typography>
                                </Grid>
                                {paymentLink.note && (
                                    <>
                                        <Grid
                                            item
                                            xs={12}
                                            style={{
                                                marginTop: '8px',
                                                paddingTop: '12px',
                                                borderTop: '1px solid #e0e0e0'
                                            }}
                                        >
                                            <Typography
                                                variant="body2"
                                                style={{ fontStyle: 'italic', color: '#666' }}
                                            >
                                                {paymentLink.note}
                                            </Typography>
                                        </Grid>
                                    </>
                                )}
                            </Grid>
                        </Box>

                        {/* Tip Section */}
                        <Box my={3}>
                            <Typography
                                variant="h6"
                                gutterBottom
                                style={{ fontWeight: 600, marginBottom: '16px' }}
                            >
                                Add a Tip (Optional)
                            </Typography>
                            <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12}>
                                    <ButtonGroup
                                        fullWidth
                                        variant="outlined"
                                        style={{ marginBottom: '12px' }}
                                    >
                                        {TIP_PRESETS.map(preset => (
                                            <Button
                                                key={preset.label}
                                                onClick={() => handlePresetClick(preset.value)}
                                                variant={
                                                    tipType === 'preset' &&
                                                    selectedPreset === preset.value
                                                        ? 'contained'
                                                        : 'outlined'
                                                }
                                                color={
                                                    tipType === 'preset' &&
                                                    selectedPreset === preset.value
                                                        ? 'primary'
                                                        : 'default'
                                                }
                                                style={{
                                                    fontWeight:
                                                        tipType === 'preset' &&
                                                        selectedPreset === preset.value
                                                            ? 600
                                                            : 400
                                                }}
                                            >
                                                {preset.label}
                                            </Button>
                                        ))}
                                        <Button
                                            onClick={handleNoTip}
                                            variant={tipType === 'none' ? 'contained' : 'outlined'}
                                            color={tipType === 'none' ? 'primary' : 'default'}
                                            style={{
                                                fontWeight: tipType === 'none' ? 600 : 400
                                            }}
                                        >
                                            No Tip
                                        </Button>
                                    </ButtonGroup>
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Or enter a custom tip amount"
                                        type="number"
                                        value={customTip}
                                        onChange={handleCustomTipChange}
                                        inputProps={{ step: '0.01', min: '0' }}
                                        placeholder="0.00"
                                        variant="outlined"
                                    />
                                </Grid>
                            </Grid>

                            {tipAmount > 0 && (
                                <Box
                                    mt={3}
                                    p={2.5}
                                    bgcolor="#e8f5e9"
                                    borderRadius="8px"
                                    style={{ border: '1px solid #81c784' }}
                                >
                                    <Grid container alignItems="center">
                                        <Grid item xs={6}>
                                            <Typography
                                                variant="body1"
                                                style={{ fontWeight: 500, color: '#2e7d32' }}
                                            >
                                                Tip Amount:
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography
                                                variant="h6"
                                                align="right"
                                                style={{ fontWeight: 600, color: '#2e7d32' }}
                                            >
                                                ${tipAmount.toFixed(2)}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Box>
                            )}

                            <Box
                                mt={3}
                                p={3}
                                bgcolor="#fff9c4"
                                borderRadius="8px"
                                style={{ border: '2px solid #fbc02d' }}
                            >
                                <Grid container alignItems="center">
                                    <Grid item xs={6}>
                                        <Typography variant="h5" style={{ fontWeight: 700 }}>
                                            Total:
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography
                                            variant="h4"
                                            align="right"
                                            style={{ fontWeight: 700, color: '#f57f17' }}
                                        >
                                            ${totalAmount.toFixed(2)}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Box>
                        </Box>

                        {/* Card Payment Form */}
                        <Box my={4}>
                            <Typography
                                variant="h6"
                                gutterBottom
                                style={{ fontWeight: 600, marginBottom: '16px' }}
                            >
                                Payment Information
                            </Typography>
                            <Box
                                p={3}
                                bgcolor="#fafafa"
                                borderRadius="8px"
                                style={{ border: '1px solid #e0e0e0' }}
                            >
                                {stripeToken && stripeToken.stripe_last4 ? (
                                    <Box>
                                        <Grid container alignItems="center" spacing={2}>
                                            <Grid item xs={12}>
                                                <Typography
                                                    variant="body1"
                                                    style={{ fontWeight: 500 }}
                                                >
                                                    Card ending in {stripeToken.stripe_last4}
                                                </Typography>
                                                <Typography variant="caption" color="textSecondary">
                                                    Card verified and ready for payment
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    onClick={() => setStripeToken(null)}
                                                    disabled={paying}
                                                >
                                                    Change Card
                                                </Button>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                ) : (
                                    <Elements stripe={stripePromise}>
                                        <CardForm
                                            onTokenCreated={setStripeToken}
                                            disabled={paying}
                                        />
                                    </Elements>
                                )}
                            </Box>
                        </Box>

                        {/* Submit Button */}
                        <Box mt={4}>
                            <Button
                                fullWidth
                                variant="contained"
                                color="primary"
                                size="large"
                                onClick={handlePayment}
                                disabled={!stripeToken || !stripeToken.stripe || paying}
                                style={{
                                    backgroundColor: company.primary_color || '#635bff',
                                    padding: '16px',
                                    fontSize: '18px',
                                    fontWeight: 600,
                                    borderRadius: '8px',
                                    textTransform: 'none',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                                }}
                            >
                                {paying
                                    ? 'Processing Payment...'
                                    : `Pay $${totalAmount.toFixed(2)}`}
                            </Button>

                            <Typography
                                variant="caption"
                                align="center"
                                display="block"
                                style={{ marginTop: '12px', color: '#999' }}
                            >
                                Your payment is secure and encrypted
                            </Typography>
                        </Box>

                        {paying && (
                            <Box mt={2}>
                                <LinearProgress style={{ borderRadius: '4px', height: '6px' }} />
                            </Box>
                        )}
                    </CardContent>
                </Card>
            </Container>
        </Box>
    );
}

export default PaymentLinkPage;
